<script>
import { mapGetters } from 'vuex';
import AddAccountModal from './components/app/AddAccountModal.vue';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
// NOTICE: KAPTION-INTEGRATION - UpdateBanner removed
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
// NOTICE: KAPTION-INTEGRATION - Kaption2FA (WhatsApp) verification warning banner
import KaptionWhatsapp2faBanner from './components/app/KaptionWhatsapp2faBanner.vue';
import KaptionDesktopNotificationsBanner from './components/app/KaptionDesktopNotificationsBanner.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-calling.WEB.1 / WEB.2: incoming-call toast + softphone
import KaptionCallLayer from './components-next/kaption/calls/KaptionCallLayer.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
// NOTICE: KAPTION-INTEGRATION - partner-provisioning.PORTAL.6
import { isKaptionPartnerPanelRoute } from 'dashboard/routes/dashboard/kaptionPartner/kaptionPartner.routes';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';

export default {
  name: 'App',

  components: {
    AddAccountModal,
    LoadingState,
    NetworkNotification,
    // NOTICE: KAPTION-INTEGRATION - UpdateBanner removed
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    // NOTICE: KAPTION-INTEGRATION
    KaptionWhatsapp2faBanner,
    KaptionDesktopNotificationsBanner,
    KaptionCallLayer,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      showAddAccountModal: false,
      // NOTICE: KAPTION-INTEGRATION - latestChatwootVersion removed
      reconnectService: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
    // NOTICE: KAPTION-INTEGRATION - partner-provisioning.PORTAL.6
    isOnKaptionPartnerPanel() {
      return isKaptionPartnerPanelRoute(this.$route?.name);
    },
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    // NOTICE: KAPTION-INTEGRATION - Rumo visual identity (rumo-rebrand.THEME.6).
    // Reactive read of the account's KAPTION_RUMO_BRAND flag; drives the
    // .rumo-brand class on <body> (see watcher below).
    isRumoBrandEnabled() {
      if (!this.currentAccountId) return false;
      return this.$store.getters['kaptionFeatureFlags/isEnabled'](
        'KAPTION_RUMO_BRAND',
        this.currentAccountId,
        this.currentUser?.id
      );
    },
    // NOTICE: KAPTION-INTEGRATION - Warm Paper layout (warm-paper.FLAG.1).
    // Drives the .warm-paper class on <body>. Every Warm Paper rule is scoped
    // under that class, so OFF renders the pre-migration layout untouched.
    isWarmPaperEnabled() {
      // NOTICE: KAPTION-INTEGRATION - partner-provisioning.PORTAL.6
      // The partner panel is new code written to the Warm Paper contract, with
      // no pre-migration layout to revert to — and its own users may hold no
      // account, so an account-scoped flag can never resolve for them. It is
      // therefore always warm, whatever the rollout flag says elsewhere.
      if (this.isOnKaptionPartnerPanel) return true;
      if (!this.currentAccountId) return false;
      return this.$store.getters['kaptionFeatureFlags/isEnabled'](
        'KAPTION_WARM_PAPER_LAYOUT',
        this.currentAccountId,
        this.currentUser?.id
      );
    },
  },

  watch: {
    currentUser() {
      // NOTICE: KAPTION-INTEGRATION - partner-provisioning.PORTAL.6
      // Having no account is the NORMAL state for partner staff, and the
      // partner panel is the surface built for them — so don't block it with
      // the "create an account to continue" modal. Everywhere else this gate
      // is unchanged.
      if (!this.hasAccounts && !this.isOnKaptionPartnerPanel) {
        this.showAddAccountModal = true;
      }
    },
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
    // NOTICE: KAPTION-INTEGRATION - rumo-rebrand.THEME.6: toggle the Rumo skin
    // class on <body> (same element that carries .dark) whenever the flag
    // resolves or flips; no reload needed.
    isRumoBrandEnabled: {
      immediate: true,
      handler(enabled) {
        document.body.classList.toggle('rumo-brand', Boolean(enabled));
      },
    },
    // NOTICE: KAPTION-INTEGRATION - warm-paper.FLAG.1: toggle the Warm Paper
    // layout class on <body> (same element as .rumo-brand / .dark) whenever the
    // flag resolves or flips; no reload needed.
    isWarmPaperEnabled: {
      immediate: true,
      handler(enabled) {
        document.body.classList.toggle('warm-paper', Boolean(enabled));
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(
        window.matchMedia('(prefers-color-scheme: dark)').matches,
        'initialization'
      );
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches, 'system');
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale } = this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);

      // NOTICE: KAPTION-INTEGRATION - Initialize Kaption Feature Flags
      if (this.currentUser?.id && this.currentAccountId) {
        await this.$store.dispatch('kaptionFeatureFlags/initialize', {
          accountId: this.currentAccountId,
          userId: this.currentUser.id,
        });
      }

      // NOTICE: KAPTION-INTEGRATION - latestChatwootVersion assignment removed
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col w-full h-screen min-h-0"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <!-- NOTICE: KAPTION-INTEGRATION - UpdateBanner removed -->
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
      <!-- NOTICE: KAPTION-INTEGRATION - Kaption2FA warning (self-hides unless flag on + unverified) -->
      <KaptionWhatsapp2faBanner v-if="hideOnOnboardingView" />
      <!-- NOTICE: KAPTION-INTEGRATION - desktop-notifications-nudge.BANNER.1
           (self-hides unless flag on + account opted in + permission unset) -->
      <KaptionDesktopNotificationsBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" v-if="Component" />
      </transition>
    </router-view>
    <!-- NOTICE: KAPTION-INTEGRATION - partner-provisioning.PORTAL.6
         Bound reactively rather than only guarded in the watcher: the
         currentUser watcher fires before the route resolves, so at that moment
         we cannot yet know we are on the panel. -->
    <AddAccountModal
      :show="showAddAccountModal && !isOnKaptionPartnerPanel"
      :has-accounts="hasAccounts"
    />
    <WootSnackbarBox />
    <NetworkNotification />
    <!-- NOTICE: KAPTION-INTEGRATION - whatsapp-calling.WEB.1 / WEB.2
         (self-hides unless KAPTION_WHATSAPP_CALLING is on) -->
    <KaptionCallLayer v-if="currentAccountId && !isOnKaptionPartnerPanel" />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

.multiselect__input {
  margin-bottom: 0px !important;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
